import React, { useRef, useState } from 'react'
import { Alert, Button, Col, Form, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { AiOutlineHome } from 'react-icons/ai'
import { Link } from 'react-router-dom'
import { gdprData, status} from './Form.data'
import loaderGif from "../../images/gif/loader.gif";
import JSONFormatter from './JSONFormatter'
import MpaasAdminPanelApi from '../../utils/MpaasAdminPanelApi'
import {ReactComponent as ArrowLeft} from "../../images/arrow_left.svg"
import {ReactComponent as Info} from "../../images/info.svg"

const ProcessGDPR = () => {

    const [error, setError] = useState("")
    const [action, setAction] = useState("")
    const [response, setResponse] = useState({
        loading: false,
        message: "",
        data: "",
        status: ""
    })
    const [showModal, setShowModal] = useState(false);
    const formRef = useRef<HTMLFormElement | null>(null)
    
    const handleSubmitConfirm = () => {
        setShowModal(false)
        if(!formRef?.current) {
            return;
        }
        const formData = new FormData(formRef.current)
        const userId = formData.get("userId")?.toString().trim()
        const clientId = formData.get("clientId")?.toString().trim()

        const htmlTagRegex = /<[^>]*>/g

        if (htmlTagRegex.test(userId ?? "") || htmlTagRegex.test(clientId ?? "")) {
            setError("HTML tags are not allowed in input fields.");
            setResponse({
                loading: false,
                message: "",
                data: "",
                status: ""
            })
            return;
        }

        if (userId) {
            const idArray = userId.split(",").map(id => id.trim()).filter(id => id !== "")

            if (idArray.length > 2) {
                setError("You can enter a maximum of 2 candidate user IDs.")
                setResponse({
                    loading: false,
                    message: "",
                    data: "",
                    status: ""
                })
                return
            }

            formData.set("userId", idArray.toString())
            formData.set("clientId", clientId ?? "")
        }

        setError("")

        setResponse((prev: any) => ({
            ...prev,
            loading: true,
        }))

        const performGDPR = action === "request" ? MpaasAdminPanelApi.requestGDPR : MpaasAdminPanelApi.deleteGDPR

        performGDPR(formData)
            .then((response: any) => {
                const {message, status, ...data} = response
                setResponse({ loading: false, message, status, data});
            })
            .catch((error) => {
                const errorMessage = error?.response?.data?.message || error?.error?.message || error?.message || "An unexpected error occurred"
                setResponse((prev) => ({
                    ...prev,
                    loading: false,
                    message: errorMessage,
                    status: status.ERROR
                }))
            })
    }

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const forbiddenChars = ['-', '+', 'e', 'E'];
        if (forbiddenChars.includes(event.key)) {
            event.preventDefault();
        }
    };
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setShowModal(true)
    }

    return (
        <div>
            <div className='breadcrumb-section'>
                <AiOutlineHome />
                <span className="small"> &gt; <Link to="/settings" className="text-muted">Settings</Link> </span>
                <span className="small"> &gt; <span className="text-muted">GDP Utility</span> </span>
                <span className="small"> &gt; <span className="text-muted">Process GDPR Request</span> </span>
            </div>

            <div className='mt-3 d-flex flex-row align-items-start'>
                <Link to="/settings"><ArrowLeft /></Link>
                <div className='d-flex flex-column nav-padding'>
                    <h5>GDPR Utility</h5>
                    <p className="text-muted">You can perform GDPR for a given candidate.</p>
                </div>
            </div>

            <div className="text-spacing"><b>Note : </b>Kindly wait for some time after clicking the button. It may take upto a few mins to complete your GDPR request</div>

            {
                error && <Row>
                    <Col xs={8}>
                        <Alert variant="danger">{error}</Alert>
                    </Col>
                    <Col></Col>
                </Row>
            }
           

            <Form onSubmit={handleSubmit} ref={formRef}>
                {gdprData?.map((data) => (
                    <Form.Group key={data.key} className='mb-4'>
                        <Row>
                            <Col>
                                <Form.Label>{data.fieldName}</Form.Label>{data.tooltip && 
                                 <OverlayTrigger placement="top" overlay={
                                    <Tooltip id="tooltip">
                                        Enter client secureProctor ID
                                    </Tooltip>}>
                                    <Info className="tooltip-margin" />
                                </OverlayTrigger>
                                
                                }
                            </Col>
                            <Col>
                                <Form.Control name={data.key} type={data.type} required={data.required} autoComplete="off" className="form-max-width" onKeyPress={data.key === "clientId" ? handleKeyPress : undefined} />
                                {data.helperText && <Form.Text className="text-muted">{data.helperText}</Form.Text>}
                            </Col>
                            <Col></Col>
                        </Row>
                    </Form.Group>
                ))}

                <Row>
                    <Col></Col>
                    <Col>
                        <Button type="submit" onClick={() => setAction("request")} className="button-margin">Request GDPR</Button>
                        <Button type="submit" onClick={() => setAction("delete")} variant="link" className="text-danger-link">Delete GDPR Request</Button>
                    </Col>
                    <Col></Col>
                </Row>
            </Form>

            {
                response.loading &&
                <div className="text-center mt-5 pt-5">
                    <img src={loaderGif} alt="response" height="150px" width="150px" />
                </div>

            }

            {!response.loading && response.message && (
                <div className="d-flex align-items-center mt-3">
                <img
                        src={response.status === status.SUCCESS ? "/success.svg" : "/error.svg"}
                        alt={response.status === status.SUCCESS ? "Success" : "Error"}
                        className="message-spacing"
                    />
                    
                    <p className={`m-0 text-${response.status===status.SUCCESS ? "success" : "danger"}`}>
                        {response.message}
                    </p>
                </div>
            )}


            {!response.loading && response.status === status.SUCCESS && response.data && <JSONFormatter jsonData={response.data} />}
            <Modal show={showModal} onHide={() => void 0} animation={false}>
                <Modal.Body>
                    Do you really want to submit this form?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => setShowModal(false)}>
                        No
                    </Button>
                    <Button variant="primary" onClick={handleSubmitConfirm}>
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default ProcessGDPR